import { MIXPANEL_EVENTS, trackEvent } from '@/lib/mixpanel';
import axios from 'axios';
import { WritableDraft } from 'immer';
import { createUseStore } from 'wired-react';
import { createStore } from 'wired-store';
import { SEARCH_TYPES, SearchRequest } from '~/dtos/plan-search';
import {
  PlanInfo,
  WARRANTYPILOT_COVERAGE_TYPE_RANKS,
  WARRANTYPILOT_COVERAGE_TYPES,
  WarrantyPilotPlanListingResponse,
} from '~/dtos/plans';
import { API_URL } from '~/modules/config';
import { selectData } from '~/modules/utilities/axios_utils';
import { handleException } from '~/modules/utilities/cross_env_utils';
import { globalStores } from '@/util/globalStores';

export interface SearchRequestStoreState {
  isLoading: boolean;
  searchRequest: SearchRequest;
  planListingResponse: WarrantyPilotPlanListingResponse;
  filteredPlanListing: PlanInfo[][];
  allVehicleOptions: Record<string, Record<string, string[]>> | null;
  yearSearchOptions: string[];
  makeSearchOptions: string[];
  modelSearchOptions: string[];
  filters: {
    providers: { id: string; label: string; checked: boolean }[];
    minPrice: number;
    currentMinPrice: number;
    maxPrice: number;
    currentMaxPrice: number;
    minMiles: number;
    currentMinMiles: number;
    maxMiles: number;
    currentMaxMiles: number;
    coverageLevel: { id: string; label: string; checked: boolean }[];
  };
}

const initialState: SearchRequestStoreState = {
  isLoading: false,
  searchRequest: {
    searchType: SEARCH_TYPES.MAKEMODEL,
    make: '',
    model: '',
    year: '',
    state: '',
    mileage: '',
    vin: '',
    licensePlate: '',
    invalidSearch: null,
  },
  planListingResponse: {
    plans: [],
    searchRequest: {
      searchType: '',
      state: '',
      mileage: '',
      invalidSearch: null,
    },
  },
  filteredPlanListing: [],
  allVehicleOptions: null,
  yearSearchOptions: [],
  makeSearchOptions: [],
  modelSearchOptions: [],
  filters: {
    providers: [],
    minPrice: 0,
    currentMinPrice: 0,
    maxPrice: 0,
    currentMaxPrice: 0,
    minMiles: 0,
    currentMinMiles: 0,
    maxMiles: 0,
    currentMaxMiles: 0,
    coverageLevel: [],
  },
};

function changeSearchRequest(changes: Partial<SearchRequestStoreState['searchRequest']>) {
  return function applyChangeRequest(draft: WritableDraft<SearchRequestStoreState>) {
    for (const key of Object.keys(changes)) {
      draft.searchRequest[key] = changes[key];
      if (key === 'year') {
        draft.searchRequest.make = '';
        draft.searchRequest.model = '';
      }
    }
  };
}

const LOCAL_STORAGE_KEY = 'searchRequestStoreState';
let localStorageState;

export const searchRequestStore = createStore(
  initialState,
  (set, get) => ({
    changeSearchRequest(changes: Partial<SearchRequestStoreState['searchRequest']>) {
      set(changeSearchRequest(changes));
    },
    changeSearchFilter(changes: Partial<SearchRequestStoreState['filters']>) {
      set(function changeSearchFilter(draft) {
        for (const key of Object.keys(changes)) {
          draft.filters[key] = changes[key];
        }

        const state = searchRequestStore.getState();

        //filter by price
        let filteredPlans = state.planListingResponse.plans
          .map((planGroup) => {
            const validPricePlans = planGroup.filter((pg) => {
              const price = pg.price?.monthly ?? 0;
              return price >= draft.filters.currentMinPrice && price <= draft.filters.currentMaxPrice;
            });
            return validPricePlans;
          })
          .filter((pg) => pg.length > 0);

        filteredPlans = filteredPlans
          .map((planGroup) => {
            const validPlanGroupMiles = planGroup.filter((pg) => {
              return (
                pg.durationMiles >= draft.filters.currentMinMiles && pg.durationMiles <= draft.filters.currentMaxMiles
              );
            });
            return validPlanGroupMiles;
          })
          .filter((pg) => pg.length > 0);

        //filter by checked providers
        filteredPlans = filteredPlans.filter((pg) => {
          const all = draft.filters.providers.find((p) => p.id === 'All');
          if (all && all.checked) return true;

          const checkedProviders = draft.filters.providers.filter((p) => p.checked).map((p) => p.id);
          return checkedProviders.includes(pg[0].provider.name);
        });

        //filter by checked coverage levels
        filteredPlans = filteredPlans.filter((planGroup) => {
          const all = draft.filters.coverageLevel.find((p) => p.id === 'All');
          if (all && all.checked) return true;

          const checkedCoverageLevel = draft.filters.coverageLevel.filter((p) => p.checked).map((p) => p.label);
          const planGroupCoverageLevel = planGroup[0].coverageLevel || '';
          return checkedCoverageLevel.includes(planGroupCoverageLevel);
        });

        draft.filteredPlanListing = filteredPlans;
      });
    },
    async getVehicleOptions() {
      try {
        const response = await axios.get(`${API_URL}/vehicle`).then(selectData);

        set(function completeGetVehicleOptions(draft) {
          draft.allVehicleOptions = response;
        });
        return response;
      } catch (error) {
        console.error('Error fetching vehicle options:', error);
        return null;
      }
    },
    async fetchPlans(requestOverrides?: Partial<SearchRequestStoreState['searchRequest']>) {
      try {
        const searchRequest = selectSearchRequest();

        if (!searchRequest.searchType || !searchRequest.state || !searchRequest.mileage) {
          throw new Error('Missing search request');
          return;
        }

        set(function setLoading(draft) {
          draft.isLoading = true;
          if (requestOverrides) {
            const applySearchRequest = changeSearchRequest(requestOverrides);
            applySearchRequest(draft);
          }
        });

        const response = await axios
          .post(`${API_URL}/plans`, JSON.stringify(searchRequest), {
            headers: {
              'Content-Type': 'application/json',
            },
          })
          .then(selectData<WarrantyPilotPlanListingResponse>);

        trackEvent(MIXPANEL_EVENTS.PLAN_LISTING_RESPONSE, {
          userInputSearchRequest: searchRequest,
          plansCount: response.plans.length,
        });

        set(function setPlanDetailsResponse(draft) {
          draft.isLoading = false;
          draft.planListingResponse = response;

          const providerPlans = response.plans.flat();

          const allPrices = providerPlans.map((plan) => plan.price?.monthly ?? 0);
          const minPrice = Math.min(...allPrices);
          const maxPrice = Math.max(...allPrices);

          draft.filters.minPrice = minPrice;
          draft.filters.currentMinPrice = minPrice;
          draft.filters.maxPrice = maxPrice;
          draft.filters.currentMaxPrice = maxPrice;

          const allDurationMiles = providerPlans.map((plan) => plan.durationMiles);
          const minMiles = Math.min(...allDurationMiles);
          const maxMiles = Math.max(...allDurationMiles);

          draft.filters.minMiles = minMiles;
          draft.filters.currentMinMiles = minMiles;
          draft.filters.maxMiles = maxMiles;
          draft.filters.currentMaxMiles = maxMiles;

          //Create provider filter options
          const values = response.plans.map((p) => {
            return p[0].provider.name;
          });
          const distinct = Array.from(new Set(values));

          const providerFilters = [
            { id: 'All', label: 'All', checked: true },
            ...distinct.sort().map((o) => {
              return { id: o, label: o, checked: true };
            }),
          ];
          draft.filters.providers = providerFilters;

          //Check if the result contains any ultimate coverage
          const containsUltimateCoverage = response.plans.some((plan) => {
            return plan[0].coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ULTIMATE;
          });

          const coverageLevelFilters = [
            { id: 'All', label: 'All', checked: !containsUltimateCoverage },
            ...Object.keys(WARRANTYPILOT_COVERAGE_TYPES)
              .map((coverageLevel) => {
                return {
                  id: WARRANTYPILOT_COVERAGE_TYPE_RANKS[coverageLevel],
                  label: WARRANTYPILOT_COVERAGE_TYPES[coverageLevel],
                  checked: containsUltimateCoverage
                    ? WARRANTYPILOT_COVERAGE_TYPES[coverageLevel] === WARRANTYPILOT_COVERAGE_TYPES.ULTIMATE
                    : true,
                };
              })
              .sort((cl) => cl.id),
          ];
          draft.filters.coverageLevel = coverageLevelFilters;

          //Apply the ultimate filter if the result contains ultimate coverages
          if (containsUltimateCoverage) {
            draft.filteredPlanListing = response.plans.filter((plan) => {
              return plan[0].coverageLevel === WARRANTYPILOT_COVERAGE_TYPES.ULTIMATE;
            });
          } else {
            draft.filteredPlanListing = response.plans;
          }
        });
      } catch (error) {
        console.error('Error fetching plans:', error);
        set(function failedLoading(draft) {
          draft.isLoading = false;
        });
      }
    },
  }),
  {
    storeName: 'searchRequestStore',
    attachTo: globalStores(),
    warnAgainstAnonymousActions: IS_DEVELOPMENT,
  },
);

try {
  if (typeof localStorage !== 'undefined') {
    localStorageState = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) ?? 'null');
    if (localStorageState) {
      localStorageState.isLoading = true;
      searchRequestStore.setState(localStorageState);
    }
  }
} catch (error) {
  localStorageState = initialState;
  handleException(error);
}

// Save state to local storage
searchRequestStore.subscribe((state) => {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }
});

// This hook is used to connect react components to the store
export const useSearchRequestStore = createUseStore(searchRequestStore);

export function selectPlanListingIsLoading(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.isLoading;
}

export function selectSearchRequest(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.searchRequest;
}

export function selectFilteredPlanListingResults(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.filteredPlanListing;
}

export function selectPlanListingResults(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.planListingResponse;
}

export function selectVehicleOptions(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.allVehicleOptions;
}

export function selectYearOptions(state: SearchRequestStoreState = searchRequestStore.getState()) {
  if (state.allVehicleOptions) {
    return Object.keys(state.allVehicleOptions).sort((a, b) => b.localeCompare(a));
  }
  return [];
}

export function selectMakeOptions(state: SearchRequestStoreState = searchRequestStore.getState()) {
  if (state.allVehicleOptions && state.searchRequest.year) {
    return Object.keys(state.allVehicleOptions[state.searchRequest.year]).sort();
  }
  return [];
}

export function selectModelOptions(state: SearchRequestStoreState = searchRequestStore.getState()) {
  if (state.allVehicleOptions && state.searchRequest.year && state.searchRequest.make) {
    return state.allVehicleOptions[state.searchRequest.year][state.searchRequest.make];
  }
  return [];
}

export function selectFilterParameters(state: SearchRequestStoreState = searchRequestStore.getState()) {
  return state.filters;
}
